import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { FilterSelection } from '@interfaces/filters/domain';
import { filtersAdapter } from '@/store/slices/filters/filtersSlice';
import { findAgeFilter } from '@/utils/filters/ageFilter';

// Local, cycle-free read of the filters catalog. Deliberately NOT imported
// from '@/store/slices/filters/selectors' — that module's top-level
// `createSelector` calls depend on `selectSelectedMarketIds` from THIS file,
// so importing back from it here creates a real ESM circular-dependency
// deadlock (the cross-imported selector resolves to `undefined` at
// module-eval time, and `createSelector` throws). Reading the catalog via
// the exported entity adapter instead has no dependency back on this file.
const selectCatalogFilters = filtersAdapter.getSelectors(
  (s: RootState) => s.activeGroup.filters.filters,
).selectAll;

export const selectSelectedMarketIds = (s: RootState): number[] =>
  s.userSelection.markets ?? [];

// Locates the age filter (see `findAgeFilter` for the rule), so a non-age
// RANGE filter listed before it is never treated as age.
export const selectAgeFilterCode = createSelector(
  [selectCatalogFilters],
  (filters) => findAgeFilter(filters)?.code,
);

// Codes of every RANGE-kind filter in the catalog (age today; others later).
export const selectRangeFilterCodes = createSelector(
  [selectCatalogFilters],
  (filters) => filters.filter((f) => f.kind === 'RANGE').map((f) => f.code),
);

// Age range is now stored directly in `userSelection.filters[]` as a RANGE
// FilterSelection — no separate `ageRange` field, no merge needed.
export const selectDraftReadyFilterSelections = createSelector(
  [(s: RootState) => (s.userSelection.filters ?? []) as unknown as FilterSelection[]],
  (filters): FilterSelection[] => filters,
);

/**
 * Extracts the age range from `userSelection.filters[]` by finding the
 * RANGE-kind FilterSelection whose code matches `selectAgeFilterCode`.
 * Returns `[min, max]` when an age range is active, or `null` otherwise.
 */
export const selectAgeRangeFromFilters = createSelector(
  [
    (s: RootState) => (s.userSelection.filters ?? []) as unknown as FilterSelection[],
    selectAgeFilterCode,
  ],
  (filters, ageCode): [number, number] | null => {
    if (!ageCode) return null;
    const ageFilter = filters.find((f) => f.code === ageCode && f.kind === 'RANGE');
    if (!ageFilter || ageFilter.kind !== 'RANGE') return null;
    return [ageFilter.range.min, ageFilter.range.max];
  },
);

// Legacy shim: flatten FilterSelection[] → number[] (option ids) for legacy
// consumers (SaveView, FileDownload, useV2Payload, legacy dataRequest
// helpers). TODO: delete when all legacy consumers migrate to V2 endpoints
// and read FilterSelection[] directly.
export const selectLegacyFilterOptionIdArray = createSelector(
  [selectDraftReadyFilterSelections, selectCatalogFilters],
  (selections, catalogFilters) => {
    const out: number[] = [];
    for (const sel of selections) {
      if (sel.kind !== 'OPTION') continue;
      const filter = catalogFilters.find((f) => f.code === sel.code);
      if (!filter) continue;
      for (const optCode of sel.options) {
        const opt = filter.options.find((o) => o.code === optCode);
        if (opt) out.push(opt.id);
      }
    }
    return out;
  },
);
